import styled from '@emotion/styled';
import { lighten } from 'polished';
import merge from 'lodash.merge';

import { remCalc, palette, font, colors } from '../../css';
import { StyledIcon } from '../Icon/Icon.css';

const backgroundColor = (className, replacePrefix = 'next-bgcolor--') => {
    const colorKey = className.replace(replacePrefix, '');
    const color = colorKey in colors ? colors[colorKey] : null;
    return {
        [className]: () =>
            color === null
                ? null
                : {
                      backgroundColor: color,
                      borderColor: color,

                      '&:hover': {
                          backgroundColor: lighten(0.05, color),
                          borderColor: lighten(0.05, color),
                      },
                  },
    };
};

const modifiers = {
    'next-button--icon-right': () => ({
        [StyledIcon]: {
            order: 1,
            marginRight: remCalc(10),
        },
        [StyledButtonValue]: {
            marginRight: remCalc(10),
        },
    }),
    'next-button--concert': () => ({
        [StyledIcon]: {
            order: 1,
        },
        img: {
            order: 1,
            marginRight: remCalc(10),
            width: remCalc(90),
        },
    }),
    'next-button--outline': () => ({
        border: `1px solid ${palette('white')}`,
        backgroundColor: 'transparent',
        transition: 'transform 125ms ease-in',

        '&:hover': {
            backgroundColor: 'transparent',
            borderColor: palette('white'),
        },
    }),
    'next-button--presse': () => ({
        border: `1px solid ${palette('black')}`,
        backgroundColor: 'transparent',
        transition: 'transform 125ms ease-in',
        color: palette('black'),

        '&:hover': {
            backgroundColor: 'transparent',
            borderColor: palette('grey', 'xxxDark'),
        },
    }),
    ...backgroundColor('next-bgcolor--arte'),
    ...backgroundColor('next-bgcolor--isWhite'),
    ...backgroundColor('next-bgcolor--darkGrey'),
    ...backgroundColor('next-bgcolor--facebook'),
};

const conditionalStyle = (classNames = '') => {
    const existingModifiers = classNames.split(' ').filter((className) => className in modifiers);
    return existingModifiers.map((modifier) => modifiers[modifier]);
};

const StyledButtonValue = styled('div')(({ isTextBlack }) => {
    return {
        fontFamily: font.family.body,
        color: !isTextBlack ? palette('white') : palette('black'),
        whiteSpace: 'nowrap',
        cursor: 'pointer',

        '&:focus, &:active': {
            outline: 'none',
        },
    };
});

const StyledWrapper = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [StyledIcon]: {
        display: 'flex',
        marginRight: remCalc(10),
    },
}));

const StyledButton = styled('button')(
    () => ({
        backgroundColor: palette('primary', 'mainNav'),
        border: `1px solid ${palette('primary', 'mainNav')}`,
        padding: remCalc(11, 30),
        textAlign: 'center',
        minWidth: remCalc(180),
        fontSize: font.size.s,
        verticalAlign: 'bottom',
        cursor: 'pointer',
        transition: 'background-color 125ms ease-in, border-color 125ms ease-in',
        outlineColor: 'transparent',
        outlineStyle: 'solid',
        outlineWidth: remCalc(2),

        '&:focus, &:active, &:hover': {
            outlineColor: palette('white'),
            outlineWidth: remCalc(2),
            outlineStyle: 'solid',
            transition: 'outline-color .3s linear',
        },

        '&:hover': {
            backgroundColor: lighten(0.05, palette('primary', 'mainNav')),
            borderColor: lighten(0.05, palette('primary', 'mainNav')),
        },
    }),
    ({ style, className }) => ({
        ...conditionalStyle(className).reduce((accumulator, modifier) => merge(accumulator, modifier()), {}),
        ...style,
    }),
);

export { StyledButton, StyledWrapper, StyledButtonValue };
